import type { TypeId } from './api';

/** One colour family per kind of identifier (board, decision 158). Never red: red is for errors. */
export type Family = 'persone' | 'contatti' | 'codici';

export const TYPE_FAMILY: Record<TypeId, Family> = {
  person: 'persone',
  'birth-date': 'persone',
  email: 'contatti',
  phone: 'contatti',
  address: 'contatti',
  username: 'contatti',
  ip: 'contatti',
  iban: 'codici',
  card: 'codici',
  'tax-code': 'codici',
  'vat-number': 'codici',
  'identity-document': 'codici',
  'health-card': 'codici',
  plate: 'codici',
};

/** The order the summary lists the types in. */
export const TYPE_ORDER: readonly TypeId[] = [
  'person',
  'birth-date',
  'email',
  'phone',
  'address',
  'username',
  'ip',
  'tax-code',
  'vat-number',
  'iban',
  'card',
  'identity-document',
  'health-card',
  'plate',
];

/** The placeholder kinds as the Markdown spells them, in the document's language. */
const KIND_TYPE: Record<string, TypeId> = {
  PERSONA: 'person',
  PERSON: 'person',
  DATA_NASCITA: 'birth-date',
  BIRTH_DATE: 'birth-date',
  EMAIL: 'email',
  TELEFONO: 'phone',
  PHONE: 'phone',
  INDIRIZZO: 'address',
  ADDRESS: 'address',
  UTENTE: 'username',
  USERNAME: 'username',
  IP: 'ip',
  CODICE_FISCALE: 'tax-code',
  TAX_CODE: 'tax-code',
  PARTITA_IVA: 'vat-number',
  VAT_NUMBER: 'vat-number',
  IBAN: 'iban',
  CARTA: 'card',
  CARD: 'card',
  DOCUMENTO: 'identity-document',
  DOCUMENTO_IDENTITA: 'identity-document',
  IDENTITY_DOCUMENT: 'identity-document',
  TESSERA_SANITARIA: 'health-card',
  HEALTH_CARD: 'health-card',
  TARGA: 'plate',
  PLATE: 'plate',
};

export const PLACEHOLDER_PATTERN = /\[([A-Z][A-Z_]*)_(\d+)\]/g;

/** The type id of a placeholder kind; `undefined` for a kind this screen does not know. */
export function typeOfKind(kind: string): TypeId | undefined {
  return KIND_TYPE[kind];
}

/** An unknown kind is shown as a code: the safest family to claim for something not recognised. */
export function familyOfKind(kind: string): Family {
  const type = typeOfKind(kind);
  return type ? TYPE_FAMILY[type] : 'codici';
}

/** The API's ids are hyphenated; tolerate the pipeline's underscored spelling too. */
export function normalizeTypeId(id: string): string {
  return id.replace(/_/g, '-');
}

export interface Occurrence {
  /** Position among all the placeholders of the text, in reading order. */
  readonly index: number;
  readonly label: string;
  readonly type: TypeId | undefined;
}

/** Every placeholder of the Markdown, in reading order. */
export function findOccurrences(markdown: string): Occurrence[] {
  const out: Occurrence[] = [];
  for (const match of markdown.matchAll(PLACEHOLDER_PATTERN)) {
    out.push({ index: out.length, label: match[0], type: typeOfKind(match[1]!) });
  }
  return out;
}

/** Tailwind classes of a placeholder, by family; the current occurrence is filled with `--focus`. */
export const FAMILY_CLASS: Record<Family, string> = {
  persone: 'bg-[var(--blue-tint)] text-[var(--blue-ink)]',
  contatti: 'bg-[var(--success-tint)] text-[var(--success)]',
  codici: 'bg-[var(--neutral-tint)] text-[var(--neutral)]',
};
